import React, { useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'

import ReusableCampaignsSummaryContent from './ReusableCampaignsSummaryContent'
import GoogleCampaignsSummaryContent from './GoogleCampaignsSummaryContent'

import { getSelectedPlatform } from '../../../helpers/handlePlatformSelection'
import { getStaticRangeLabel } from '../../../features/components/DateRange/helpers'

import { clearCampaignGraphs, getCampaignGraphs } from '../../../modules/actions/campaignsSummary'
import {
  campaignGraphsErrorSelector,
  campaignGraphsIsLoadingSelector,
  campaignGraphsSelector,
  campaignGraphsWasLoadedSelector,
  campaignsSummaryDateRangeSelector
} from '../../../modules/selectors/campaignsSummary'
import { campaignSelector } from '../../../modules/selectors/campaigns'
import { selectedAdAccountsSelector } from '../../../modules/selectors/adAccounts'

import { DATES_FORMAT_BE, formatDateFullYear } from '../../../constants/dates'
import {
  DV_360_PLATFORM,
  FACEBOOK_PLATFORM,
  GOOGLE_PLATFORM,
  TIKTOK_PLATFORM
} from '../../../constants/selectLists/platformList'
import { CAMPAIGNS_GRAPH_BREAKDOWNS_BY_PLATFORM } from '../../../constants/campaigns'

import useSummaryPagesStyles from '../../../styles/common/summaryPages'

const CampaignsSummaryContent = () => {
  const summaryPagesClasses = useSummaryPagesStyles()

  const dispatch = useDispatch()

  const adsSectionRef = useRef(null)
  const reportsSectionRef = useRef(null)

  const selectedPlatform = getSelectedPlatform()

  const campaign = useSelector(campaignSelector)
  const campaignsSummaryDateRange = useSelector(campaignsSummaryDateRangeSelector)
  const { [selectedPlatform]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)

  const { date_preset: datePreset, startDate, endDate } = campaignsSummaryDateRange

  const { id: selectedAdAccountId, currency_symbol: currencySymbol } = selectedAdAccount

  const { id: campaignId, name: campaignName } = campaign

  const formattedDateRangeLabel =
    startDate && endDate && `${formatDateFullYear(startDate)} - ${formatDateFullYear(endDate)}`

  const staticRangeLabel = getStaticRangeLabel(datePreset)

  const DateRangeLabel = useMemo(
    () =>
      (staticRangeLabel || formattedDateRangeLabel) && (
        <div className={summaryPagesClasses.dateRangeLabel}>{staticRangeLabel || formattedDateRangeLabel}</div>
      ),
    [staticRangeLabel, formattedDateRangeLabel, summaryPagesClasses.dateRangeLabel]
  )

  const scrollToAdsSectionHandler = useCallback(() => {
    adsSectionRef &&
      adsSectionRef.current &&
      window.scrollTo({ behavior: 'smooth', top: adsSectionRef.current.offsetTop - 30 })
  }, [])

  const scrollToReportsSectionHandler = useCallback(() => {
    reportsSectionRef &&
      reportsSectionRef.current &&
      window.scrollTo({ behavior: 'smooth', top: reportsSectionRef.current.offsetTop - 30 })
  }, [])

  const getGraphsHandler = useCallback(() => {
    if (campaignId) {
      dispatch(
        getCampaignGraphs(selectedPlatform, {
          account: selectedAdAccountId,
          campaign: campaignId,
          date_from: startDate && format(startDate, DATES_FORMAT_BE),
          date_to: endDate && format(endDate, DATES_FORMAT_BE),
          date_preset: datePreset,
          insights: [
            'conversions',
            'impressions',
            'ctr',
            'clicks',
            'cpa',
            'lead'
            // todo PBR-1057 uncomment when failing request is fixed
            // 'complete_registration',
            // 'landing_page_view'
          ],
          breakdown: CAMPAIGNS_GRAPH_BREAKDOWNS_BY_PLATFORM[selectedPlatform],
          sort_reverse: false
        })
      )
    }
  }, [dispatch, selectedPlatform, selectedAdAccountId, campaignId, startDate, endDate, datePreset])

  const reusableSummaryGraphsProps = useMemo(
    () => ({
      getGraphs: getGraphsHandler,
      clearGraphsAction: clearCampaignGraphs,
      graphsSelector: campaignGraphsSelector,
      graphsIsLoadingSelector: campaignGraphsIsLoadingSelector,
      graphsErrorSelector: campaignGraphsErrorSelector,
      graphsWasLoadedSelector: campaignGraphsWasLoadedSelector,
      scrollRef: reportsSectionRef,
      currencySymbol: currencySymbol,
      RightPanel: DateRangeLabel
    }),
    [DateRangeLabel, currencySymbol, getGraphsHandler]
  )

  return (
    <>
      {(selectedPlatform === FACEBOOK_PLATFORM ||
        selectedPlatform === TIKTOK_PLATFORM ||
        selectedPlatform === DV_360_PLATFORM) && (
        <ReusableCampaignsSummaryContent
          selectedPlatform={selectedPlatform}
          campaignName={campaignName}
          DateRangeLabel={DateRangeLabel}
          adsSectionRef={adsSectionRef}
          reportsSectionRef={reportsSectionRef}
          scrollToAdsSectionHandler={scrollToAdsSectionHandler}
          scrollToReportsSectionHandler={scrollToReportsSectionHandler}
          getGraphsHandler={getGraphsHandler}
          reusableSummaryGraphsProps={reusableSummaryGraphsProps}
        />
      )}
      {selectedPlatform === GOOGLE_PLATFORM && (
        <GoogleCampaignsSummaryContent
          campaignName={campaignName}
          DateRangeLabel={DateRangeLabel}
          adsSectionRef={adsSectionRef}
          reportsSectionRef={reportsSectionRef}
          scrollToAdsSectionHandler={scrollToAdsSectionHandler}
          scrollToReportsSectionHandler={scrollToReportsSectionHandler}
          getGraphsHandler={getGraphsHandler}
          reusableSummaryGraphsProps={reusableSummaryGraphsProps}
        />
      )}
    </>
  )
}

export default CampaignsSummaryContent
